import React, { useCallback } from "react";

import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form-7";
import { useApolloClient } from "@apollo/client";

import {
  Alert,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Stack,
  StackDivider,
  Text,
} from "@chakra-ui/react";

import useTranslations from "../../../hooks/useTranslations";
import useNotification from "../../../hooks/useNotification";
import {
  useDeleteCredential,
  useUserUpdatePassword,
} from "../../../graphql/generated/definitions";

import LoginLayout from "../layout/LoginLayout";
import PasswordInput from "../components/PasswordInput";
import ValidationBox from "../components/ValidationBox";

export type ResetPasswordFormInputs = {
  password: string;
  confirmationPassword: string;
};

export default function ResetPasswordPage() {
  const t = useTranslations();
  const { notifySuccess } = useNotification();

  const history = useHistory();

  const {
    register,
    watch,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<ResetPasswordFormInputs>({ mode: "onChange" });

  const [userUpdatePassword, { error: userUpdatePasswordError }] =
    useUserUpdatePassword();

  const apolloClient = useApolloClient();
  const [deleteCredential] = useDeleteCredential();

  const watchedPassword = watch("password", "");

  const onSubmit = useCallback(
    async ({ password, confirmationPassword }: ResetPasswordFormInputs) => {
      try {
        await userUpdatePassword({
          variables: {
            password,
            passwordConfirmation: confirmationPassword,
          },
        });

        notifySuccess({
          title: t("resetPassword.passwordResetSuccessfully") as string,
        });
        await deleteCredential();
        apolloClient.resetStore();
      } catch {
        // Should not append
      }
    },
    [history]
  );

  // Password validation
  const hasMinimumEightCharacterLong = watchedPassword.length >= 8;
  const hasUppercaseCharacter = !!watchedPassword.match(/[A-Z]/);
  const hasLowercaseCharacter = !!watchedPassword.match(/[a-z]/);
  const hasNumber = !!watchedPassword.match(/[0-9]/);
  const hasSpecialCharacter = !!watchedPassword.match(/[^A-Za-z0-9]/);

  const isMinimumPasswordComplexityReached =
    hasMinimumEightCharacterLong &&
    hasUppercaseCharacter &&
    hasLowercaseCharacter &&
    hasNumber &&
    hasSpecialCharacter;

  return (
    <LoginLayout title={t("resetPassword.passwordReset") as string}>
      <Stack divider={<StackDivider color="gray.200" />} spacing="6">
        <Text fontWeight="semibold">{t("resetPassword.enterNewPassword")}</Text>
        <form id="update-password-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing="5">
            <FormControl
              id="password"
              isRequired
              isInvalid={!!errors?.password?.message}
            >
              <FormLabel>{t("resetPassword.newPassword")}</FormLabel>
              <PasswordInput
                size="lg"
                // This looks rather ugly but native form validation block the one made by RHF
                isRequired={false}
                aria-required="true"
                {...register("password", {
                  required: t("forms.errors.required") as string,
                })}
              />
              <Grid templateColumns="repeat(5, 1fr)" gap={2} pt="5">
                <ValidationBox
                  isValid={hasMinimumEightCharacterLong}
                  text={t("resetPassword.verifyPassword.chars") as string}
                  count={8}
                />
                <ValidationBox
                  isValid={hasUppercaseCharacter}
                  text={t("resetPassword.verifyPassword.uppercase") as string}
                  count={1}
                />
                <ValidationBox
                  isValid={hasLowercaseCharacter}
                  text={t("resetPassword.verifyPassword.lowercase") as string}
                  count={1}
                />
                <ValidationBox
                  isValid={hasNumber}
                  text={t("resetPassword.verifyPassword.number") as string}
                  count={1}
                />
                <ValidationBox
                  isValid={hasSpecialCharacter}
                  text={
                    t("resetPassword.verifyPassword.specialchars") as string
                  }
                  count={1}
                />
              </Grid>
              <FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
            </FormControl>
            <FormControl
              id="confirm-password"
              isRequired
              isDisabled={!isMinimumPasswordComplexityReached}
              isInvalid={!!errors?.confirmationPassword?.message}
            >
              <FormLabel>{t("resetPassword.confirmNewPassword")}</FormLabel>
              <PasswordInput
                size="lg"
                // This looks rather ugly but native form validation block the one made by RHF
                isRequired={false}
                aria-required="true"
                {...register("confirmationPassword", {
                  required: t("forms.errors.required") as string,
                  validate: (value) =>
                    value === watchedPassword
                      ? true
                      : (t("resetPassword.passwordMismatch") as string),
                })}
              />
              <FormErrorMessage>
                {errors?.confirmationPassword?.message}
              </FormErrorMessage>
            </FormControl>

            {userUpdatePasswordError && (
              <Alert status="warning" id="reset-password-form-error">
                {t("resetPassword.error")}
              </Alert>
            )}

            <Stack spacing="6" pt="4">
              <Button
                disabled={!isValid}
                type="submit"
                isFullWidth
                colorScheme="primary"
              >
                {t("resetPassword.reset")}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </LoginLayout>
  );
}
